import { FormControl, Validators } from '@angular/forms';
import {
  MessageTypeEnum,
  MessageStatusEnum,
  DeliveryStatusEnum
} from '../../enums';

// ============================================================================
// SHARED FIELD OPTIONS INTERFACE
// ============================================================================

export interface FieldOptions {
  required?: boolean;
}

// ============================================================================
// MESSAGE TYPE FIELD
// ============================================================================

/**
 * Field: Message Type
 * Type: MessageTypeEnum (Email | Notification)
 *
 * @param options.required - If true, adds Validators.required (default: true for create, false for search)
 * @param initialValue - Initial value (default: 'Email' if required, null if not required)
 *
 * @example Create form (required):
 * createMessageTypeField({ required: true })
 *
 * @example Search form (optional):
 * createMessageTypeField({ required: false })
 */
export function createMessageTypeField(
  options: FieldOptions & { required: true },
  initialValue?: MessageTypeEnum
): FormControl<MessageTypeEnum>;
export function createMessageTypeField(
  options?: FieldOptions & { required?: false },
  initialValue?: MessageTypeEnum | null
): FormControl<MessageTypeEnum | null>;
export function createMessageTypeField(
  options: FieldOptions = {},
  initialValue?: MessageTypeEnum | null
): FormControl<MessageTypeEnum | null> | FormControl<MessageTypeEnum> {
  const { required = false } = options;

  if (required) {
    return new FormControl<MessageTypeEnum>(
      initialValue ?? MessageTypeEnum.Email,
      {
        nonNullable: true,
        validators: [Validators.required]
      }
    );
  }

  return new FormControl<MessageTypeEnum | null>(initialValue ?? null);
}

// ============================================================================
// DELIVERY STATUS FIELD (Search/Filter only)
// ============================================================================

/**
 * Field: Delivery Status (Tab selection)
 * Type: DeliveryStatusEnum (sent | draft)
 * Required: Yes (always non-nullable)
 * Default: 'sent'
 */
export function createDeliveryStatusField(
  initialValue: DeliveryStatusEnum = Object.values(DeliveryStatusEnum)[0]
): FormControl<DeliveryStatusEnum> {
  return new FormControl<DeliveryStatusEnum>(initialValue, {
    nonNullable: true
  });
}

// ============================================================================
// TEXT FIELDS
// ============================================================================

/**
 * Field: Search Text
 * Type: string | null
 * Required: No
 * Default: null
 */
export function createSearchTextField(
  initialValue: string | null = null
): FormControl<string | null> {
  return new FormControl<string | null>(initialValue);
}

/**
 * Field: Subject
 * Type: string
 * Required: Configurable (default: true)
 * Validation: Max length 255 characters
 *
 * @param options.required - If true, adds Validators.required (default: true)
 */
export function createMessageSubjectField(
  options: FieldOptions = {},
  initialValue = ''
): FormControl<string> {
  const { required = true } = options;
  const validators = [Validators.maxLength(255)];

  if (required) {
    validators.unshift(Validators.required);
  }

  return new FormControl<string>(initialValue, {
    nonNullable: true,
    validators
  });
}

/**
 * Field: Message Content
 * Type: string (HTML supported for email)
 * Required: Configurable (default: true)
 *
 * @param options.required - If true, adds Validators.required (default: true)
 */
export function createMessageContentField(
  options: FieldOptions = {},
  initialValue = ''
): FormControl<string> {
  const { required = true } = options;
  const validators = required ? [Validators.required] : [];

  return new FormControl<string>(initialValue, {
    nonNullable: true,
    validators
  });
}

// ============================================================================
// ARRAY FIELDS (TARGETING & FILTERING)
// ============================================================================

/**
 * Field: Send To Users
 * Type: string[] (array of user IDs or ['all'])
 * Required: Configurable (default: true)
 * Default: ['all']
 *
 * @param options.required - If true, adds Validators.required (default: true)
 */
export function createSendToUsersField(
  options: FieldOptions = {},
  initialValue: string[] = ['all']
): FormControl<string[]> {
  const { required = true } = options;
  const validators = required ? [Validators.required] : [];

  return new FormControl<string[]>(initialValue, {
    nonNullable: true,
    validators
  });
}

/**
 * Field: State Codes (unified for Send To States and State Codes Filter)
 * Type: string[] (array of state codes)
 * Required: No
 * Default: []
 */
export function createStateCodesField(
  initialValue: string[] = []
): FormControl<string[]> {
  return new FormControl<string[]>(initialValue, {
    nonNullable: true
  });
}

/**
 * Field: Send To Roles
 * Type: string[] (array of role names)
 * Required: No
 * Default: []
 */
export function createSendToRolesField(
  initialValue: string[] = []
): FormControl<string[]> {
  return new FormControl<string[]>(initialValue, {
    nonNullable: true
  });
}

/**
 * Field: Message Statuses Filter
 * Type: MessageStatusEnum[] (array of statuses)
 * Required: No
 * Default: []
 */
export function createMessageStatusesField(
  initialValue: MessageStatusEnum[] = []
): FormControl<MessageStatusEnum[]> {
  return new FormControl<MessageStatusEnum[]>(initialValue, {
    nonNullable: true
  });
}

// ============================================================================
// SCHEDULE FIELDS
// ============================================================================

/**
 * Field: Schedule To
 * Type: string | null (ISO date string)
 * Required: No
 * Used for scheduling messages in the future
 */
export function createScheduleToField(
  initialValue: string | null = null
): FormControl<string | null> {
  return new FormControl<string | null>(initialValue);
}

/**
 * Field: Save As Draft
 * Type: boolean
 * Required: No
 * Default: false
 */
export function createSaveAsDraftField(
  initialValue = false
): FormControl<boolean> {
  return new FormControl<boolean>(initialValue, {
    nonNullable: true
  });
}

// ============================================================================
// PAGINATION FIELDS
// ============================================================================

/**
 * Field: Page Number
 * Type: number
 * Required: Yes (always non-nullable)
 * Default: 1
 */
export function createPageNumberField(
  initialValue: number = 1
): FormControl<number> {
  return new FormControl<number>(initialValue, {
    nonNullable: true
  });
}

/**
 * Field: Page Size
 * Type: number
 * Required: Yes (always non-nullable)
 * Default: 10
 */
export function createPageSizeField(
  initialValue: number = 10
): FormControl<number> {
  return new FormControl<number>(initialValue, {
    nonNullable: true
  });
}
